import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getPreferences, updatePreferences } from "@/lib/api";
import { PLATFORMS, OUTPUT_FORMATS, FIT_METHODS, FIT_BG, getPlatform } from "@/lib/presets";
import { Check } from "@phosphor-icons/react";

export default function Settings() {
  const { user, loading } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    getPreferences().then(setPrefs);
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!prefs) return <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 text-zinc-400">Loading…</div>;

  const platform = getPlatform(prefs.default_platform);

  const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await updatePreferences(prefs);
      setPrefs(r);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Settings</span>
      <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-1">Preferences</h1>
      <p className="text-zinc-400 mt-2">Defaults applied every time you open the Studio.</p>

      <div className="mt-10 space-y-5">
        <Row label="Default platform">
          <select data-testid="default-platform" className="input-base" value={prefs.default_platform} onChange={(e) => {
            const p = getPlatform(e.target.value);
            update({ default_platform: e.target.value, default_preset: p.presets[0].id });
          }}>
            {PLATFORMS.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </Row>

        <Row label="Default size preset">
          <select data-testid="default-preset" className="input-base" value={prefs.default_preset} onChange={(e) => update({ default_preset: e.target.value })}>
            {platform.presets.map((p) => (<option key={p.id} value={p.id}>{p.label} — {p.w}×{p.h}</option>))}
          </select>
        </Row>

        <Row label="Default fit method">
          <select data-testid="default-fit-method" className="input-base" value={prefs.default_fit_method} onChange={(e) => update({ default_fit_method: e.target.value })}>
            {FIT_METHODS.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
          </select>
        </Row>

        <Row label="Default fit background">
          <select data-testid="default-fit-bg" className="input-base" value={prefs.default_fit_bg} onChange={(e) => update({ default_fit_bg: e.target.value })}>
            {FIT_BG.map((b) => (<option key={b.id} value={b.id}>{b.label}</option>))}
          </select>
        </Row>

        <Row label="Default output format">
          <select data-testid="default-format" className="input-base" value={prefs.default_format} onChange={(e) => update({ default_format: e.target.value })}>
            {OUTPUT_FORMATS.map((f) => (<option key={f.id} value={f.id}>{f.label}</option>))}
          </select>
        </Row>

        <Row label={`Default quality (${prefs.default_quality})`}>
          <input
            type="range"
            min="10"
            max="100"
            value={prefs.default_quality}
            onChange={(e) => update({ default_quality: parseInt(e.target.value) })}
            className="w-full accent-[#CCFF00]"
            data-testid="default-quality"
          />
        </Row>

        <div className="pt-6 flex items-center gap-3">
          <button data-testid="save-prefs-button" onClick={save} disabled={saving} className="btn-volt">
            {saving ? "Saving…" : saved ? (<><Check size={16} weight="bold" /> Saved</>) : "Save preferences"}
          </button>
          {saved && <span className="text-xs text-[#CCFF00] font-mono">Synced to your account</span>}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="surface p-5 grid grid-cols-1 md:grid-cols-3 items-center gap-4">
      <div className="text-sm text-zinc-300 font-medium">{label}</div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}
